"use client";

import React, { useState } from "react";

import Image from "next/image";

import { Badge } from "@/components/atoms/Badge/Badge";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Text } from "@/components/atoms/Text";
import { HorizontalVendorProfileCard } from "@/components/organisms/VendorProfileCard/HorizontalVendorProfileCard";
import CircleCheckFilled16 from "@/icons/__generated__/CheckCircleFilled/CheckCircleFilled16";
import StarFilledIcon from "@/icons/__generated__/StarFilled/StarFilled16";
import analyticsIntegrations from "~/app/analytics/analytics-client";
import { cloudinaryVendorImageLoader } from "~/utils/image-loaders";

import { formatPriceType } from "../../../utils/vendor/pricing.util";

import { RecommendedVendor } from "./RecommendedVendors.types";

type VendorListProps = {
  isExpanded: boolean;
  hasMore: boolean;
  listingId: string;
  vendors: RecommendedVendor[];
  onToggleExpanded: () => void;
};

export const VendorList = ({
  isExpanded,
  hasMore,
  listingId,
  vendors,
  onToggleExpanded,
}: VendorListProps) => {
  const [profileImgErrors, setProfileImgErrors] = useState<Set<string>>(
    new Set(),
  );

  const handleProfileImgError = (vendorId: string) => {
    setProfileImgErrors((prev) => new Set(prev).add(vendorId));
  };

  const handleVendorClick = (vendorId: string, vendorCategory: string) => {
    analyticsIntegrations.listingVendorClicked(
      listingId,
      vendorId,
      vendorCategory,
    );
    window.open(`/vendors/profile/${vendorId}`, "_blank", "noopener");
  };

  const formatReviewStars = (stars: number): string => stars.toFixed(1);

  const formatPricingStatement = (vendorAmount: number, vendorType: string) => {
    return `From $${vendorAmount}/${formatPriceType(vendorType)}`;
  };

  const getVendorBadgeLabel = (vendor: RecommendedVendor) => {
    if (vendor.host_recommended) return "Host recommended";
    if (vendor.top_vendor) return "Top Chicago vendor";
    return null;
  };

  return (
    <div>
      {vendors.map((vendor, i) => {
        const vendorPrice = vendor.prices?.[0]?.amount;
        const vendorPriceType = vendor.prices?.[0]?.type;
        const showPrices = !!vendorPrice && !!vendorPriceType;

        return (
          <div
            className="tw-mb-32 md:tw-mb-20"
            key={`vendor-${vendor._id}-${i}`}
            role="button"
            tabIndex={0}
            onClick={() => handleVendorClick(vendor._id, vendor.category_id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleVendorClick(vendor._id, vendor.category_id);
              }
            }}
          >
            <HorizontalVendorProfileCard
              image={
                vendor.images?.[0]?.external_id ||
                vendor.cover_img?.external_id ||
                ""
              }
              TLZone={
                getVendorBadgeLabel(vendor) ? (
                  <Badge appearance="gray" className="tw-bg-gray-900/70">
                    <CircleCheckFilled16 />
                    <Text
                      typeface="body-1-m"
                      element="p"
                      className="tw-ml-6 !tw-opacity-100"
                    >
                      {getVendorBadgeLabel(vendor)}
                    </Text>
                  </Badge>
                ) : null
              }
              TRZone={null}
              zone1={
                <div className="tw-flex tw-justify-between">
                  <div className="tw-flex tw-gap-12">
                    <div className="tw-w-40 tw-h-40 tw-rounded-full tw-overflow-hidden tw-shrink-0 tw-border tw-border-gray-400">
                      {profileImgErrors.has(vendor._id) ? (
                        <Skeleton />
                      ) : (
                        <Image
                          width={40}
                          height={40}
                          alt=""
                          src={vendor.profile_img?.external_id}
                          loader={cloudinaryVendorImageLoader}
                          onError={() => handleProfileImgError(vendor._id)}
                        />
                      )}
                    </div>
                    <div className="tw-min-w-0">
                      <Text
                        typeface="body-2-m"
                        className="tw-underline tw-mb-0 tw-truncate"
                        element="h4"
                      >
                        {vendor.title}
                      </Text>
                      <Text
                        typeface="body-1"
                        element="p"
                        className="tw-truncate"
                      >
                        {vendor.category_display_name}
                      </Text>
                      {showPrices && (
                        <Text
                          typeface="body-1"
                          element="p"
                          className="tw-mt-4 tw-truncate"
                        >
                          {formatPricingStatement(vendorPrice, vendorPriceType)}
                        </Text>
                      )}
                    </div>
                  </div>
                  {vendor.review_count > 0 && (
                    <div className="tw-flex tw-gap-4">
                      <StarFilledIcon />
                      <Text
                        typeface="body-1"
                        element="p"
                        className="tw-truncate"
                      >
                        {formatReviewStars(vendor.review_stars)} (
                        {vendor.review_count})
                      </Text>
                    </div>
                  )}
                </div>
              }
              zone2={null}
              zone3={
                <div className="tw-text-gray-600">
                  <Text
                    typeface="body-1-long"
                    className="tw-text-gray-600 tw-line-clamp-3"
                  >
                    {vendor.description}
                  </Text>
                  <Text
                    typeface="body-1"
                    className="tw-underline tw-text-gray-600 tw-mt-8"
                  >
                    Read more
                  </Text>
                </div>
              }
            />
          </div>
        );
      })}
      {hasMore && (
        <button onClick={onToggleExpanded} className="tw-mt-4">
          <Text typeface="body-1-m" className="tw-underline">
            {isExpanded ? "Show less" : "Show more"}
          </Text>
        </button>
      )}
    </div>
  );
};
